import { Keplr, Window as KeplrWindow } from "@keplr-wallet/types";
import { Psbt } from "bitcoinjs-lib";
import { Eip1193Provider } from "ethers";
import {
  Address,
  BTC,
  Msg,
  Network,
  Signer,
  Simulation,
  THOR,
  TxResult,
} from "rujira.js";
import { CosmosAdapter } from "./cosmos";
import { Eip6963Adapter } from "./eip6963";
import { walletType } from "./utils";
import * as utxo from "./utxo";
import { UtxoContext } from "./utxo";

interface BitcoinProvider {
  connect: () => Promise<{ address: string; publicKey: string }>;
  signPsbt: (
    psbtHex: string,
    options?: { autofinalized?: boolean }
  ) => Promise<string>;
}

const uniqueAddresses = (addresses: Address[]): Address[] => {
  const result = new Map<string, Address>();
  for (const address of addresses) {
    const current = result.get(address.address);
    result.set(
      address.address,
      current
        ? {
            ...current,
            networks: [...new Set([...current.networks, ...address.networks])],
          }
        : address
    );
  }
  return [...result.values()];
};

declare global {
  interface Window extends KeplrWindow {
    okxwallet?: Eip1193Provider & {
      bitcoin?: BitcoinProvider;
      keplr?: Keplr & { isOkWallet: true };
    };
  }
}

export class OkxAdapter implements Signer {
  readonly requiresMessageSigningHandoff = true;
  readonly messageSigningScheme = "eip191" as const;
  private owners = new Map<string, "evm" | "cosmos" | "bitcoin">();
  private generation = 0;
  private changeListener?: () => void;

  constructor(
    private e: Eip6963Adapter,
    private c: CosmosAdapter,
    private b?: BitcoinProvider | (() => BitcoinProvider | undefined)
  ) {}

  private bitcoin() {
    return typeof this.b === "function" ? this.b() : this.b;
  }

  // Keep network provenance: a THOR address returned by the EVM leg needs
  // EIP-712, whereas a native Cosmos address must keep its Cosmos signer.
  private key(network: Network, address: string) {
    return `${network}:${network === THOR ? address.toLowerCase() : address}`;
  }

  private invalidate = () => {
    this.generation += 1;
    this.owners.clear();
  };

  async connect(): Promise<Address[]> {
    this.invalidate();
    const generation = this.generation;
    const bitcoin = this.bitcoin();
    const results = await Promise.allSettled([
      Promise.resolve().then(() => this.e.connect()),
      Promise.resolve().then(() => this.c.connect()),
      bitcoin
        ? Promise.resolve()
            .then(() => bitcoin.connect())
            .then((res) => [{ address: res.address, networks: [BTC] }])
        : Promise.resolve([]),
    ]);
    if (generation !== this.generation) {
      throw new Error("OKX accounts changed while connecting. Connect again.");
    }
    const legs = ["evm", "cosmos", "bitcoin"] as const;
    const addresses: Address[] = [];
    results.forEach((result, index) => {
      if (result.status !== "fulfilled") return;
      for (const address of result.value) {
        addresses.push(address);
        for (const network of address.networks) {
          const key = this.key(network, address.address);
          // EVM is deliberately first when both adapters report one THOR
          // address; never switch signer because a request was rejected.
          if (!this.owners.has(key)) this.owners.set(key, legs[index]);
        }
      }
    });
    if (addresses.length === 0) {
      const failure = results.find((result) => result.status === "rejected");
      throw failure?.status === "rejected"
        ? failure.reason
        : new Error("No OKX accounts available");
    }
    return uniqueAddresses(addresses);
  }

  private cosmosSigner(msg: Msg) {
    // Inner adapters retain their exact connected address. Never let case
    // normalization turn a THOR lookup into an unrelated EVM transaction path.
    if (
      msg.account.network === THOR &&
      msg.account.address !== msg.account.address.toLowerCase()
    ) {
      throw new Error("Use the connected OKX account address. Connect again.");
    }
    const owner = this.owners.get(
      this.key(msg.account.network, msg.account.address)
    );
    if (msg.account.network === THOR && owner === "evm") return this.e;
    if (owner === "cosmos") return this.c;
    throw new Error("This OKX account is not connected. Connect again.");
  }

  // Private sign-in uses the EVM leg's verified personal signature, not an
  // arbitrary native Cosmos address that happens to use the same prefix.
  signMessage(chainId: string, address: string, message: string) {
    if (this.owners.get(this.key(THOR, address)) !== "evm") {
      throw new Error(
        "This OKX account cannot sign Private Mode messages. Connect its EVM account first."
      );
    }
    return this.e.signMessage(chainId, address, message);
  }
  async simulate(msg: Msg): Promise<Simulation> {
    const type = walletType(msg.account);
    switch (type) {
      case "cosmos":
        return this.cosmosSigner(msg).simulate(msg);
      case "evm":
        return this.e.simulate(msg);
      case "sol":
      case "ton":
      case "xrp":
      case "tron":
        throw new Error(`${type} not supported by OKX Wallet`);

      default:
        return utxo.simulate(new UtxoContext(msg.account.address), msg);
    }
  }
  async signAndBroadcast(simulation: Simulation, msg: Msg): Promise<TxResult> {
    const type = walletType(msg.account);
    switch (type) {
      case "cosmos":
        return this.cosmosSigner(msg).signAndBroadcast(simulation, msg);
      case "evm":
        return this.e.signAndBroadcast(simulation, msg);
      case "sol":
      case "ton":
      case "xrp":
      case "tron":
        throw new Error(`${type} not supported by OKX Wallet`);

      default:
        if (!this.bitcoin())
          throw new Error("OKX Bitcoin provider unavailable");
        return utxo.signAndBroadcast(
          new UtxoContext(msg.account.address, async (psbt) => {
            const res = await this.bitcoin()?.signPsbt(psbt.toHex(), {
              autofinalized: true,
            });
            if (!res) throw new Error("Okx extension not found");
            return Psbt.fromHex(res);
          }),
          msg
        );
    }
  }
  onChange = (cb: () => void) => {
    if (typeof window !== "undefined" && this.changeListener) {
      window.removeEventListener("keplr_keystorechange", this.changeListener);
    }
    this.changeListener = () => {
      this.invalidate();
      cb();
    };
    this.e.onChange(this.changeListener);
    this.c.onChange?.(this.changeListener);
    if (typeof window !== "undefined") {
      window.addEventListener("keplr_keystorechange", this.changeListener);
    }
  };
  isAvailable() {
    return this.e.isAvailable() || this.c.isAvailable() || !!this.bitcoin();
  }

  public networks(): Network[] {
    return [BTC, ...this.c.networks(), ...this.e.networks()];
  }
}

// Construction reads `window.*` so it must be gated for SSR.
const provider: OkxAdapter =
  typeof window === "undefined"
    ? (null as unknown as OkxAdapter)
    : new OkxAdapter(
        new Eip6963Adapter("com.okex.wallet", true, () => window.okxwallet),
        new CosmosAdapter(() => window.okxwallet?.keplr),
        () => window.okxwallet?.bitcoin
      );

export default provider;
